import React, { useEffect, useState, useCallback, useRef } from "react";
import { Container, Row, Col } from "react-bootstrap";
import {
  Paper,
  Chip,
  TextField,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { useNavigate, useLocation } from "react-router-dom";
import apiService, { readPagedTotalFromEnvelope } from "../../services/api";
import Pagination from "../../components/ui/Pagination";
import SortableTable, { SortableColumn } from "../../components/common/SortableTable";
import { usePagination } from "../../hooks/usePagination";
import { DATE_FORMATS } from "../../constants";
import ActionIcon from "../../../src/assets/icons/ActionIcon.svg";
// TODO: restore when re-enabling row edit
import EditIcon from "../../../src/assets/icons/EditIcon.svg";
import useCodeCommon from "../../hooks/useCodeCommon";
import SearchableSelectInput from "../../components/forms/SearchableSelectInput";
// TODO: restore when re-enabling row edit
import useRBAC, { ROLES } from "../../hooks/useRBAC";
import CustomLoading from "../../components/common/CustomLoading";
import { formatMobileNumber } from "../../utils/formatters";
import { useAppSelector } from "../../hooks/useAuth";
import { getHubId } from "../../utils/cookieUtils";
import useRoleBasedFiltering from "../../hooks/useRoleBasedFiltering";

dayjs.extend(utc);

function parseGetAllBookingsResponse(response: unknown): { items: unknown[]; total: number } {
  const resp = response as Record<string, unknown> | null | undefined;
  if (resp == null || typeof resp !== "object") {
    return { items: [], total: 0 };
  }

  const dataField = resp.data;
  /** Same shape as payment search: `{ data: Booking[], totalCount, pageNumber, ... }` */
  if (Array.isArray(dataField)) {
    const items = dataField as unknown[];
    return { items, total: readPagedTotalFromEnvelope(resp, items.length) };
  }

  const envelope = dataField;
  if (envelope == null || typeof envelope !== "object") {
    return { items: [], total: 0 };
  }
  const e = envelope as Record<string, unknown>;
  const itemsRaw = e.items ?? (e as { Items?: unknown[] }).Items;
  const items = Array.isArray(itemsRaw) ? itemsRaw : [];
  return { items, total: readPagedTotalFromEnvelope(e, items.length) };
}

interface BookingRow {
  id: string;
  bookingMasterId?: string;
  bookingId: string;
  /** Renter / customer user id from list API — fallback for edit-booking KYC when trip details omit userid */
  renterUserId?: number | null;
  userName: string;
  mobileNo: string; // Formatted mobile number for display
  rawMobileNo?: string; // Raw mobile number for searching
  plan: string;
  vehicle: string;
  vehicleNumber: string;
  registrationNumber: string;
  startDate: string;
  endDate: string;
  bookingStatus: string;
  rideStatus: string;
  payment: string;
  hubid?: string | number;
  tripStatus?: number | null; // Raw trip status id from API (used to hide edit when 2, 3, 4)
  hubmasterid?: string | number;
}

const StatusChip = ({ value }: { value: string }) => {
  const getChipStyles = (status: string) => {
    switch (status) {
      case "Pending":
        return {
          backgroundColor: "rgba(255, 248, 220, 1)",
          color: "rgba(184, 134, 11, 1)",
          fontWeight: 600,
        };
      case "Approve":
        return {
          backgroundColor: "rgba(212, 255, 199, 1)",
          color: "rgba(31, 113, 13, 1)",
          fontWeight: 600,
        };
      case "Completed":
        return {
          backgroundColor: "rgba(212, 255, 199, 1)",
          color: "rgba(31, 113, 13, 1)",
          fontWeight: 600,
        };
      case "Rejected":
        return {
          backgroundColor: "rgba(255, 227, 227, 1)",
          color: "rgba(197, 0, 0, 1)",
          fontWeight: 600,
        };
      case "Ride In Progress":
        return {
          backgroundColor: "rgba(227, 242, 253, 1)",
          color: "rgba(25, 118, 210, 1)",
          fontWeight: 600,
        };
      default:
        return {
          backgroundColor: "rgba(245, 245, 245, 1)",
          color: "rgba(117, 117, 117, 1)",
          fontWeight: 600,
        };
    }
  };

  return <Chip label={value} size="small" sx={getChipStyles(value)} />;
};

const BookingManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const initialFilters = location.state?.filters || {};
  const initialPage = location.state?.page || 0;
  const shouldRefetchFromState = location.state?.shouldRefetch || false;

  const [filters, setFilters] = useState<Record<string, string>>(
    initialFilters.filters || {}
  );
  const [rows, setRows] = useState<BookingRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [shouldRefetchOnFocus, setShouldRefetchOnFocus] = useState(false);
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>(() => {
    const from = initialFilters.dateRange?.[0];
    const to = initialFilters.dateRange?.[1];
    if (from && to) {
      return [dayjs(from), dayjs(to)];
    }
    return [dayjs().startOf("month"), dayjs()];
  });
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  const dateRangeRef = useRef(dateRange);
  dateRangeRef.current = dateRange;

  const {
    options: tripStatusOptions,
    loading: tripStatusLoading,
  } = useCodeCommon("TripStatus");

  const { user } = useAppSelector((state) => state.auth);
  const isSuperAdmin = user?.roleid === 1;
  const { filterByRole } = useRoleBasedFiltering<BookingRow>();

  const { hasAnyRole } = useRBAC();

  // TODO: restore when re-enabling row edit (uncomment `handleEditBooking` below too)
  const canShowEditIcon = hasAnyRole([
    ROLES.SUPER_ADMIN,
    ROLES.ADMIN,
    ROLES.HUB_MANAGER,
    ROLES.HUB_CLUSTER_MANAGER,
  ]);

  const pagination = usePagination({
    totalItems: totalCount,
    initialPage: initialPage,
    initialRowsPerPage: 10,
  });

  const getTripStatusDisplayName = useCallback(
    (tripstatusid: number | null | undefined): string => {
      const statusId = tripstatusid === null || tripstatusid === undefined ? 1 : tripstatusid;
      const statusOption = tripStatusOptions.find(
        (option) => Number.parseInt(option.codevalue, 10) === statusId
      );
      return statusOption ? statusOption.codedisplayname : "Pending";
    },
    [tripStatusOptions]
  );

  const resolveRideStatusForApi = useCallback((): number | null => {
    const raw = filters.status?.trim();
    if (!raw) return null;
    const n = Number.parseInt(raw, 10);
    return Number.isFinite(n) ? n : null;
  }, [filters.status]);

  /** Super admin: 0 = all hubs. Hub manager / hub-scoped roles: cookie hub id (matches payment search). */
  const resolveHubMasterIdForBookings = useCallback((): number => {
    if (isSuperAdmin) return 0;
    const hid = getHubId();
    return hid != null && Number.isFinite(Number(hid)) ? Number(hid) : 0;
  }, [isSuperAdmin]);

  const fetchBookings = useCallback(async (pageOverride?: number) => {
      try {
        setLoading(true);
        const pageIndex = pageOverride !== undefined ? pageOverride : pagination.page;
        const pageSize = Math.max(1, Number(pagination.rowsPerPage) || 10);
        /** API uses 1-based `pageNumber` (first page = 1). UI `pagination.page` is 0-based. */
        const pageNumber = pageIndex + 1;
        const [from, to] = dateRangeRef.current;
        const payload = {
          fromDate:
            from && to ? from.startOf("day").utcOffset(0, true).toISOString() : null,
          toDate:
            from && to ? to.endOf("day").utcOffset(0, true).toISOString() : null,
          searchText: (filters.searchTerm ?? "").trim(),
          rideStatus: resolveRideStatusForApi(),
          pageNumber,
          pageSize,
          hubmasterid: resolveHubMasterIdForBookings(),
        };

        const response = await apiService.getBookings(payload);
        const { items, total } = parseGetAllBookingsResponse(response);

        const mapped: BookingRow[] = items.map((item: any, idx: number) => {
          const status = getTripStatusDisplayName(item.tripstatus);
          const rawMobileNo = item.phonenumber ? String(item.phonenumber) : "";

          const getValueOrNA = (value: any): string => {
            if (value === null || value === undefined || value === "") {
              return "N/A";
            }
            return String(value);
          };

          const renterUidRaw =
            item.userid ??
            item.userId ??
            item.UserId ??
            item.createdby ??
            item.CreatedBy ??
            item.customerid ??
            item.customerId;
          const renterUidNum = Number(renterUidRaw);
          const renterUserId =
            renterUidRaw != null && renterUidRaw !== "" && Number.isFinite(renterUidNum) && renterUidNum > 0
              ? renterUidNum
              : null;

          const masterRaw =
            item.bookingmasterid ??
            item.bookingMasterId ??
            item.BookingMasterId;
          const displayBookingId =
            masterRaw != null && String(masterRaw).trim() !== ""
              ? String(masterRaw).trim()
              : "N/A";

          return {
            id: String(item.bookingmasterid ?? idx),
            bookingMasterId: String(item.bookingmasterid ?? idx),
            bookingId: displayBookingId,
            renterUserId,
            userName: getValueOrNA(item.name),
            mobileNo: formatMobileNumber(item.phonenumber) || "N/A",
            rawMobileNo,
            plan: getValueOrNA(item.planname),
            vehicle: getValueOrNA(item.vehiclename),
            vehicleNumber: getValueOrNA(item.vehiclenum),
            registrationNumber: getValueOrNA(item.registrationnumber),
            startDate: DATE_FORMATS.formatDate(item.pickupstarttime ?? ""),
            endDate: DATE_FORMATS.formatDate(item.bookingPlanModel?.planenddate ?? ""),
            bookingStatus: "",
            rideStatus: status,
            payment: item.bookingPlanModel?.bookingamount ? `₹${item.bookingPlanModel.bookingamount}` : "",
            hubid: item.hubid ?? item.hubmasterid,
            hubmasterid: item.hubmasterid ?? item.hubid,
            tripStatus: item.tripstatus != null ? Number(item.tripstatus) : null,
          };
        });

        const scopedRows = filterByRole(mapped, "hubmasterid");
        setTotalCount(total);
        setRows(scopedRows);
      } catch {
        setRows([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    }, [
      filters.searchTerm,
      resolveRideStatusForApi,
      resolveHubMasterIdForBookings,
      filterByRole,
      pagination.page,
      pagination.rowsPerPage,
      getTripStatusDisplayName,
    ]);

  useEffect(() => {
    if (tripStatusLoading || tripStatusOptions.length === 0) return;
    void fetchBookings();
  }, [tripStatusLoading, tripStatusOptions.length, fetchBookings]);

  useEffect(() => {
    if (!shouldRefetchFromState || tripStatusLoading || tripStatusOptions.length === 0) return;
    void fetchBookings();
    navigate(location.pathname, { replace: true, state: { ...location.state, shouldRefetch: false } });
  }, [shouldRefetchFromState, tripStatusLoading, tripStatusOptions.length, fetchBookings, navigate, location.pathname]);

  useEffect(() => {
    const onFocus = () => {
      if (shouldRefetchOnFocus && !tripStatusLoading && tripStatusOptions.length > 0) {
        void fetchBookings();
        setShouldRefetchOnFocus(false);
      }
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [shouldRefetchOnFocus, tripStatusLoading, tripStatusOptions.length, fetchBookings]);

  const handleViewDetails = (bookingId: string) => {
    const selected = rows.find((r) => r.id === bookingId);
    if (!selected) return;

    setShouldRefetchOnFocus(true);

    const filtersToPass = {
      filters,
      dateRange: [dateRange[0]?.toISOString() || null, dateRange[1]?.toISOString() || null],
    };

    navigate(
      `/booking-management/details?bookingId=${encodeURIComponent(
        selected.bookingMasterId || bookingId
      )}&userName=${encodeURIComponent(selected.userName || "")}`,
      { state: { filters: filtersToPass, page: pagination.page } }
    );
  };

  // TODO: restore when re-enabling row edit
  const handleEditBooking = (row: BookingRow) => {
    const selected = rows.find((r) => r.id === row.id);
    if (!selected) return;

    setShouldRefetchOnFocus(true);

    const bookingId = selected.bookingMasterId || selected.id;
    const suffix = bookingId ? `_${bookingId}` : "";
    try {
      sessionStorage.removeItem(`editBooking_step${suffix}`);
      sessionStorage.removeItem(`editBooking_hubId${suffix}`);
      sessionStorage.removeItem(`editBooking_modelId${suffix}`);
      sessionStorage.removeItem(`editBooking_vehicleId${suffix}`);
      sessionStorage.removeItem(`editBooking_planId${suffix}`);
    } catch {
      // ignore
    }

    const filtersToPass = {
      filters,
      dateRange: [dateRange[0]?.toISOString() || null, dateRange[1]?.toISOString() || null],
    };

    navigate(
      `/booking-management/edit-booking?bookingId=${encodeURIComponent(String(bookingId))}`,
      {
        state: {
          bookingId,
          bookingMasterId: selected.bookingMasterId,
          userName: selected.userName,
          mobileNo: selected.mobileNo,
          customerId: String(selected.bookingMasterId ?? selected.id),
          renterUserId: selected.renterUserId ?? undefined,
          hubId: selected.hubid != null ? Number(selected.hubid) : undefined,
          filters: { filters: filtersToPass, dateRange: filtersToPass.dateRange },
          page: pagination.page,
        },
      }
    );
  };

  const handleFilterChange = (key: string, value: string) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    pagination.resetPage();
    navigate(location.pathname, {
      replace: true,
      state: {
        ...((location.state as object) || {}),
        filters: {
          filters: newFilters,
          dateRange: [
            dateRange[0]?.toISOString() || null,
            dateRange[1]?.toISOString() || null,
          ],
        },
        page: 0,
      },
    });
  };

  const handleDateRangeChange = (newRange: [any, any]) => {
    const start = newRange[0] ? (newRange[0].format ? newRange[0] : dayjs(newRange[0])) : null;
    const end = newRange[1] ? (newRange[1].format ? newRange[1] : dayjs(newRange[1])) : null;
    setDateRange([start, end]);
  };

  const handleApplyDateRange = () => {
    setDatePickerOpen(false);
    if (dateRange[0] && dateRange[1]) {
      pagination.resetPage();
      if (!tripStatusLoading && tripStatusOptions.length > 0) {
        void fetchBookings(0);
      }
      navigate(location.pathname, {
        replace: true,
        state: {
          ...((location.state as object) || {}),
          filters: {
            filters,
            dateRange: [
              dateRange[0]?.toISOString() || null,
              dateRange[1]?.toISOString() || null,
            ],
          },
          page: 0,
        },
      });
    }
  };

  useEffect(() => {
    try {
      const exportFilters = {
        filterModel: {
          fromDate:
            dateRange[0] && dateRange[1]
              ? dateRange[0].startOf("day").utcOffset(0, true).toISOString()
              : null,
          toDate:
            dateRange[0] && dateRange[1]
              ? dateRange[1].endOf("day").utcOffset(0, true).toISOString()
              : null,
          searchText: filters.searchTerm || "",
          rideStatus: resolveRideStatusForApi(),
        },
      };
      localStorage.setItem("bookingExportFilters", JSON.stringify(exportFilters));
    } catch {}
  }, [dateRange, filters.searchTerm, filters.status, resolveRideStatusForApi]);

  const columns: SortableColumn<BookingRow>[] = [
    { id: "bookingId", label: "Booking ID" },
    { id: "userName", label: "User Name" },
    { id: "mobileNo", label: "Mobile No." },
    { id: "plan", label: "Plan" },
    { id: "vehicle", label: "Vehicle" },
    { id: "vehicleNumber", label: "Vehicle Number" },
    { id: "registrationNumber", label: "Registration Number" },
    { id: "startDate", label: "Start Date" },
    { id: "endDate", label: "End Date" },
    {
      id: "rideStatus",
      label: "Ride Status",
      align: "center",
      render: (row) => <StatusChip value={row.rideStatus} />,
    },
    { id: "payment", label: "Payment", align: "center" },
    {
      id: "id" as keyof BookingRow,
      label: "Action",
      align: "center",
      sortable: false,
      render: (row) => {
        // TODO: Temporarily disabled for publish — uncomment block below to restore row edit icon.
        const showEdit = canShowEditIcon && ![6, 3, 4].includes(Number(row.tripStatus));
        const editSlot =
          showEdit ? (
            <Button variant="text" sx={{ p: 0, minWidth: "auto" }} onClick={() => handleEditBooking(row)}>
              <img src={EditIcon} alt="Edit" width={30} height={30} />
            </Button>
          ) : canShowEditIcon ? (
            <Box sx={{ width: 30, height: 30, flexShrink: 0 }} aria-hidden />
          ) : null;

        return (
          <Box sx={{ display: "flex", gap: 1, justifyContent: "center", alignItems: "center" }}>
            <Button variant="text" sx={{ p: 0, minWidth: "auto" }} onClick={() => handleViewDetails(row.id)}>
              <img src={ActionIcon} alt="Action" width={30} height={30} />
            </Button>
            {editSlot}
          </Box>
        );
      },
    },
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container fluid className="px-0">
        <Paper className="mb-3 p-3 border-0 rounded-0 shadow-none">
          <Row className="g-3 align-items-end">
            <Col xs={12} md={3}>
              <TextField
                label="Date Range"
                value={
                  dateRange[0] && dateRange[1]
                    ? `${dateRange[0].format("DD/MM/YYYY")} - ${dateRange[1].format("DD/MM/YYYY")}`
                    : ""
                }
                onClick={() => setDatePickerOpen(true)}
                size="small"
                fullWidth
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <Button size="small" onClick={() => setDatePickerOpen(true)}>
                      📅
                    </Button>
                  ),
                }}
              />
              <Dialog open={datePickerOpen} onClose={() => setDatePickerOpen(false)} fullWidth>
                <DialogTitle>Select Date Range</DialogTitle>
                <DialogContent>
                  <Box display="flex" gap={2} justifyContent="center" mt={2}>
                    <DatePicker
                      label="From"
                      value={dateRange[0]}
                      onChange={(v) => handleDateRangeChange([v, dateRange[1]])}
                      slotProps={{ textField: { size: "small" } }}
                      format="DD/MM/YYYY"
                    />
                    <DatePicker
                      label="To"
                      value={dateRange[1]}
                      onChange={(v) => handleDateRangeChange([dateRange[0], v])}
                      slotProps={{ textField: { size: "small" } }}
                      format="DD/MM/YYYY"
                    />
                  </Box>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setDatePickerOpen(false)}>Cancel</Button>
                  <Button variant="contained" onClick={handleApplyDateRange}>
                    Apply
                  </Button>
                </DialogActions>
              </Dialog>
            </Col>

            <Col xs={12} md={2}>
              <SearchableSelectInput
                field="status"
                formData={filters}
                onChange={handleFilterChange}
                options={tripStatusOptions.map(option => ({
                  label: option.codedisplayname,
                  value: option.codevalue
                }))}
                label="Status"
                placeholder="Select Status"
                loading={tripStatusLoading}
              />
            </Col>


            <Col xs={12} md={3}>
              <TextField
                label="Search"
                placeholder="Search by Booking ID, Name, Mobile, Vehicle, Vehicle Number, or Registration Number"
                size="small"
                fullWidth
                value={filters.searchTerm ?? ""}
                onChange={(e) => handleFilterChange("searchTerm", e.target.value)}
              />
            </Col>
          </Row>
        </Paper>

        <Paper className="border-0 rounded-0 shadow-none">
          {loading ? (
            <CustomLoading message="Fetching Booking Table Data..." />
          ) : (
            <SortableTable
              data={rows}
              columns={columns}
              emptyMessage="No bookings found"
            />
          )}
        </Paper>

        <Pagination
          page={pagination.page}
          rowsPerPage={pagination.rowsPerPage}
          totalItems={totalCount}
          totalPages={pagination.totalPages}
          startItem={pagination.startItem}
          endItem={pagination.endItem}
          rowsPerPageOptions={[5, 10, 25, 50]}
          showRowsPerPage
          onRowsPerPageChange={pagination.handleChangeRowsPerPage}
          onGoToNextPage={pagination.goToNextPage}
          onGoToPreviousPage={pagination.goToPreviousPage}
        />
      </Container>
    </LocalizationProvider>
  );
};

export default BookingManagementPage;
